import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';
import { 
  Settings as SettingsIcon, 
  Shield, 
  Database, 
  Sliders,
  Save,
  RefreshCw,
  Cpu,
  Zap,
  Cloud,
  Check,
  Gauge,
  Globe,
  MessageSquare,
  Phone,
  AlertTriangle
} from 'lucide-react';

export function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [rateLimits, setRateLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Local state for editable settings
  const [enablePII, setEnablePII] = useState(true);
  const [enableCaching, setEnableCaching] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [cacheThreshold, setCacheThreshold] = useState(0.85);

  const fetchRateLimits = async () => {
    try {
      const res = await api.getRateLimitStats();
      setRateLimits(res.data);
    } catch (err) {
      console.error('Failed to fetch rate limits:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, tiersRes] = await Promise.all([
          api.getSettings(),
          api.getTiers()
        ]);
        setSettings(settingsRes.data);
        setTiers(tiersRes.data.tiers);
        
        // Set local state
        setEnablePII(settingsRes.data.enable_pii_detection);
        setEnableCaching(settingsRes.data.enable_caching);
        setConfidenceThreshold(settingsRes.data.confidence_threshold);
        setCacheThreshold(settingsRes.data.cache_similarity_threshold);
        
        // Fetch rate limits
        await fetchRateLimits();
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Refresh rate limits every 5 seconds
    const interval = setInterval(fetchRateLimits, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings({
        enable_pii_detection: enablePII,
        enable_caching: enableCaching,
        confidence_threshold: confidenceThreshold,
        cache_similarity_threshold: cacheThreshold,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const tierIcons = {
    tier1_local_fast: Cpu,
    tier2_local_smart: Zap,
    tier3_cloud: Cloud,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-48 bg-secondary rounded" />
          <div className="h-64 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            SETTINGS
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure the tiered inference system
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : saved ? (
            <Check className="h-4 w-4 mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature Toggles */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" />
              Feature Toggles
            </CardTitle>
            <CardDescription className="text-xs">
              Enable or disable system features
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-amber-500/10">
                  <Shield className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <Label className="text-sm">PII Detection</Label>
                  <p className="text-xs text-muted-foreground">
                    Detect and sanitize personally identifiable information
                  </p>
                </div>
              </div>
              <Switch 
                checked={enablePII} 
                onCheckedChange={setEnablePII}
                data-testid="pii-toggle"
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-emerald-500/10">
                  <Database className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <Label className="text-sm">Semantic Caching</Label>
                  <p className="text-xs text-muted-foreground">
                    Cache similar queries to reduce API calls
                  </p>
                </div>
              </div>
              <Switch 
                checked={enableCaching} 
                onCheckedChange={setEnableCaching}
                data-testid="caching-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Thresholds */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sliders className="h-4 w-4" />
              Routing Thresholds
            </CardTitle>
            <CardDescription className="text-xs">
              Fine-tune the routing behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Confidence Threshold</Label>
                <span className="text-sm font-mono text-primary">
                  {(confidenceThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[confidenceThreshold * 100]}
                onValueChange={([v]) => setConfidenceThreshold(v / 100)}
                min={50}
                max={95}
                step={5}
                className="w-full"
                data-testid="confidence-slider"
              />
              <p className="text-xs text-muted-foreground">
                Queries below this confidence will be escalated to a higher tier
              </p>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Cache Similarity Threshold</Label>
                <span className="text-sm font-mono text-primary">
                  {(cacheThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[cacheThreshold * 100]}
                onValueChange={([v]) => setCacheThreshold(v / 100)}
                min={70}
                max={99}
                step={1}
                className="w-full"
                data-testid="cache-slider"
              />
              <p className="text-xs text-muted-foreground">
                Queries must be this similar to use cached responses
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Rate Limiting */}
        <Card className="border-border bg-card lg:col-span-2" data-testid="rate-limits-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gauge className="h-4 w-4 text-orange-400" />
              Rate Limiting
            </CardTitle>
            <CardDescription className="text-xs">
              Per-channel request limits to protect the gateway
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rateLimits ? (
              <div className="space-y-6">
                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground">Total Requests</div>
                    <div className="text-xl font-mono font-bold">{rateLimits.total_requests?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground">Blocked</div>
                    <div className="text-xl font-mono font-bold text-red-400">{rateLimits.limited_requests || 0}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground">Block Rate</div>
                    <div className="text-xl font-mono font-bold">{rateLimits.limit_rate || 0}%</div>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground">Status</div>
                    <div className={`text-xl font-mono font-bold ${rateLimits.enabled ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {rateLimits.enabled ? 'Active' : 'Disabled'}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Per-Channel Limits */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {rateLimits.limits && Object.entries(rateLimits.limits).map(([channel, limits]) => {
                    const channelStats = rateLimits.by_channel?.[channel] || { total: 0, limited: 0 };
                    const channelIcons = {
                      web: Globe,
                      api: Zap,
                      telegram: MessageSquare,
                      whatsapp: Phone
                    };
                    const Icon = channelIcons[channel] || Globe;
                    const usagePercent = limits.requests_per_minute > 0 
                      ? Math.min(100, (channelStats.total / limits.requests_per_hour) * 100) 
                      : 0;
                    
                    return (
                      <div 
                        key={channel}
                        className="p-4 rounded-lg border border-border bg-card"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Icon className="h-4 w-4 text-cyan-400" />
                          <span className="font-medium text-sm capitalize">{channel}</span>
                          {channelStats.limited > 0 && (
                            <AlertTriangle className="h-3 w-3 text-amber-400 ml-auto" />
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Per Minute</span>
                            <span className="font-mono">{limits.requests_per_minute} req</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Per Hour</span>
                            <span className="font-mono">{limits.requests_per_hour} req</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Burst</span>
                            <span className="font-mono">{limits.burst_capacity} req</span>
                          </div>
                          
                          <Separator />
                          
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Usage</span>
                              <span className="font-mono">{channelStats.total} / {limits.requests_per_hour}</span>
                            </div>
                            <Progress value={usagePercent} className="h-1" />
                          </div>
                          
                          {channelStats.limited > 0 && (
                            <div className="text-xs text-amber-400">
                              {channelStats.limited} requests blocked
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Loading rate limit configuration...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Model Configuration */}
        <Card className="border-border bg-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Tier Configuration
            </CardTitle>
            <CardDescription className="text-xs">
              Current model assignments for each inference tier
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tiers.map((tier) => {
                const Icon = tierIcons[tier.id] || Cpu;
                return (
                  <div 
                    key={tier.id}
                    className="p-4 rounded-lg border border-border"
                    style={{ borderLeftColor: tier.color, borderLeftWidth: '3px' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="h-4 w-4" style={{ color: tier.color }} />
                      <span className="font-medium text-sm">{tier.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {tier.description}
                    </p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Model</span>
                        <span className="font-mono">{tier.model}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Target Latency</span>
                        <span className="font-mono">{tier.target_latency_ms}ms</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cost/1K tokens</span>
                        <span className="font-mono">${tier.cost_per_1k_tokens}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SettingsPage;
